import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Home, Menu, X } from 'lucide-react';
import { quizQuestions } from '../data/quizQuestions';
import '../styles/Quiz.css';

// Format phone number to (123)456-7890 format
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');
  
  // Format based on length
  if (phoneNumber.length === 0) return '';
  if (phoneNumber.length <= 3) return `(${phoneNumber}`;
  if (phoneNumber.length <= 6) return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3)}`;
  return `(${phoneNumber.slice(0, 3)})${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
};

const Quiz = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [menuOpen, setMenuOpen] = useState(false);
  const [contactInfo, setContactInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });

  // Get IP-based location on mount
  useEffect(() => {
    fetchIPLocation();
  }, []);

  const fetchIPLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      setContactInfo(prev => ({
        ...prev,
        location: `${data.city}, ${data.region}, ${data.country_name}`
      }));
    } catch (error) {
      console.error('Error fetching location:', error);
      setContactInfo(prev => ({ ...prev, location: 'Unknown' }));
    }
  };

  const totalSteps = quizQuestions.length + 1; // +1 for contact form
  const progress = ((currentStep + 1) / totalSteps) * 100;

  const handleContactSubmit = (e) => {
    e.preventDefault();
    if (contactInfo.name && contactInfo.email) {
      setCurrentStep(1);
    }
  };

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    
    // Auto-advance to next question after a brief delay
    setTimeout(() => {
      if (currentStep < totalSteps - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        // Last question - submit
        submitQuiz();
      }
    }, 600); // 600ms delay for visual feedback
  };

  const handleNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      // Submit quiz and go to results
      submitQuiz();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const submitQuiz = () => {
    const quizData = {
      contactInfo,
      answers,
      variant: new URLSearchParams(window.location.search).get('variant') || 'A',
      timestamp: new Date().toISOString()
    };
    
    // Store in sessionStorage for results page
    sessionStorage.setItem('quizResults', JSON.stringify(quizData));
    navigate('/results');
  };

  const isStepComplete = () => {
    if (currentStep === 0) {
      return contactInfo.name && contactInfo.email;
    }
    const currentQuestion = quizQuestions[currentStep - 1];
    return answers[currentQuestion.id] !== undefined;
  };

  // Render contact form (step 0)
  if (currentStep === 0) {
    return (
      <div className="quiz-container">
        <div className="quiz-header">
          <div className="quiz-logo-container">
            <img src="/logo.png" alt="LP Global Holdings" className="quiz-logo-img" />
          </div>
          <nav className="quiz-nav">
            <Link to="/" className="nav-home-link">
              <Home size={20} />
              Home
            </Link>
          </nav>
        </div>

        <div className="quiz-content">
          <div className="progress-bar">
            <div className="progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div className="quiz-step">
            <h2 className="step-title">Let's Get Started</h2>
            <p className="step-subtitle">We'll send your personalized results to this email.</p>

            <form onSubmit={handleContactSubmit} className="contact-form">
              <div className="form-field">
                <label>Full Name *</label>
                <input
                  type="text"
                  value={contactInfo.name}
                  onChange={(e) => setContactInfo({ ...contactInfo, name: e.target.value })}
                  placeholder="John Smith"
                  required
                />
              </div>

              <div className="form-field">
                <label>Email Address *</label>
                <input
                  type="email"
                  value={contactInfo.email}
                  onChange={(e) => setContactInfo({ ...contactInfo, email: e.target.value })}
                  placeholder="john@business.com"
                  required
                />
              </div>

              <div className="form-field">
                <label>Phone Number (Optional)</label>
                <input
                  type="tel"
                  value={contactInfo.phone}
                  onChange={(e) => setContactInfo({ ...contactInfo, phone: formatPhoneNumber(e.target.value) })}
                  placeholder="(555)123-4567"
                  maxLength={14}
                />
              </div>

              <button type="submit" className="btn-primary" disabled={!isStepComplete()}>
                Start Assessment
                <ArrowRight size={20} />
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Render quiz questions
  const currentQuestion = quizQuestions[currentStep - 1];
  const questionNumber = currentStep;
  
  // Alternate background colors for visual distinction
  const isEvenQuestion = questionNumber % 2 === 0;
  const questionBgClass = isEvenQuestion ? 'quiz-container quiz-alt-bg' : 'quiz-container';

  return (
    <div className={questionBgClass}>
      <div className="quiz-header">
        <div className="quiz-logo-container">
          <img src="/logo.png" alt="LP Global Holdings" className="quiz-logo-img" />
        </div>
        <nav className="quiz-nav">
          <Link to="/" className="nav-home-link">
            <Home size={20} />
            Home
          </Link>
        </nav>
      </div>

      <div className="quiz-content">
        <div className="progress-bar">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <div className="quiz-step">
          <div className="question-number">Question {questionNumber} of {quizQuestions.length}</div>
          <h2 className="step-title">{currentQuestion.question}</h2>
          {currentQuestion.subtitle && (
            <p className="step-subtitle">{currentQuestion.subtitle}</p>
          )}
          {currentQuestion.definition && (
            <div className="question-definition">💡 {currentQuestion.definition}</div>
          )}

          <div className="answer-options">
            {currentQuestion.type === 'yesno' && (
              <div className="yesno-options">
                <button
                  className={`option-btn ${answers[currentQuestion.id] === true ? 'selected' : ''}`}
                  onClick={() => handleAnswer(currentQuestion.id, true)}
                >
                  Yes
                </button>
                <button
                  className={`option-btn ${answers[currentQuestion.id] === false ? 'selected' : ''}`}
                  onClick={() => handleAnswer(currentQuestion.id, false)}
                >
                  No
                </button>
              </div>
            )}

            {currentQuestion.type === 'multiple' && (
              <div className="multiple-options">
                {currentQuestion.options.map((option, idx) => (
                  <button
                    key={idx}
                    className={`option-card ${answers[currentQuestion.id] === option.value ? 'selected' : ''}`}
                    onClick={() => handleAnswer(currentQuestion.id, option.value)}
                  >
                    <div className="option-label">{option.label}</div>
                    {option.description && (
                      <div className="option-description">{option.description}</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {currentQuestion.type === 'text' && (
              <textarea
                className="text-answer"
                value={answers[currentQuestion.id] || ''}
                onChange={(e) => handleAnswer(currentQuestion.id, e.target.value)}
                placeholder="Share as much or as little as you'd like..."
                rows={4}
              />
            )}
          </div>

          <div className="quiz-navigation">
            <button onClick={handleBack} className="btn-secondary nav-btn">
              <ArrowLeft size={20} />
              Back
            </button>
            <button 
              onClick={handleNext} 
              className="btn-primary nav-btn"
              disabled={!isStepComplete()}
            >
              {currentStep === totalSteps - 1 ? 'See Results' : 'Next'}
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
